// ============================================
// MOCK DATA GENERATOR for Analytics Dashboard
// Eventually replace with real API calls
// ============================================

import dayjs from "dayjs";

// Seed-based pseudo-random for consistent data
function seededRandom(seed) {
    let s = seed;
    return () => {
        s = (s * 16807 + 0) % 2147483647;
        return (s - 1) / 2147483646;
    };
}

// Generate daily visitors for the past N months
export function generateDailyVisitors(monthsBack = 3) {
    const days = [];
    const today = dayjs();
    const start = today.subtract(monthsBack, "month").startOf("month");
    const totalDays = today.diff(start, "day") + 1;
    const rng = seededRandom(42);

    for (let i = 0; i < totalDays; i++) {
        const date = start.add(i, "day");
        const dow = date.day(); // 0=Sun, 6=Sat
        const isWeekend = dow === 0 || dow === 6;

        // Base traffic: weekdays higher than weekends
        let base = isWeekend ? 120 + rng() * 80 : 200 + rng() * 150;

        // Monthly trend: growth over time
        const monthIndex = date.diff(start, "month", true);
        base *= 1 + monthIndex * 0.08;

        // Seasonal spikes (simulate campaigns or holidays)
        if (date.date() >= 10 && date.date() <= 14) base *= 1.15;
        if (date.date() >= 25 && date.date() <= 28) base *= 1.2;

        // Random daily variance
        base *= 0.85 + rng() * 0.3;

        days.push({
            date: date.format("YYYY-MM-DD"),
            dateObj: date,
            visitors: Math.round(base),
            pageViews: Math.round(base * (2.5 + rng() * 1.5)),
            uniqueVisitors: Math.round(base * (0.65 + rng() * 0.15)),
            bounceRate: Math.round((35 + rng() * 25) * 10) / 10,
            avgSessionDuration: Math.round((120 + rng() * 180) * 10) / 10, // seconds
            newUsers: Math.round(base * (0.3 + rng() * 0.2)),
            returningUsers: Math.round(base * (0.4 + rng() * 0.15)),
        });
    }
    return days;
}

// Aggregate daily data into monthly summaries
export function aggregateMonthly(dailyData) {
    const months = {};
    for (const d of dailyData) {
        const key = d.date.slice(0, 7); // "YYYY-MM"
        if (!months[key]) {
            months[key] = {
                month: key,
                label: dayjs(d.date).format("MMM YYYY"),
                shortLabel: dayjs(d.date).format("MMM"),
                visitors: 0,
                pageViews: 0,
                uniqueVisitors: 0,
                newUsers: 0,
                returningUsers: 0,
                days: 0,
                bounceRateSum: 0,
                sessionDurationSum: 0,
            };
        }
        const m = months[key];
        m.visitors += d.visitors;
        m.pageViews += d.pageViews;
        m.uniqueVisitors += d.uniqueVisitors;
        m.newUsers += d.newUsers;
        m.returningUsers += d.returningUsers;
        m.bounceRateSum += d.bounceRate;
        m.sessionDurationSum += d.avgSessionDuration;
        m.days++;
    }

    return Object.values(months).map(m => ({
        ...m,
        avgBounceRate: Math.round((m.bounceRateSum / m.days) * 10) / 10,
        avgSessionDuration: Math.round(m.sessionDurationSum / m.days),
    }));
}

// Aggregate into weekly data
export function aggregateWeekly(dailyData) {
    const weeks = {};
    for (const d of dailyData) {
        const weekStart = dayjs(d.date).startOf("week").format("YYYY-MM-DD");
        if (!weeks[weekStart]) {
            weeks[weekStart] = {
                week: weekStart,
                label: dayjs(weekStart).format("MMM D"),
                visitors: 0,
                pageViews: 0,
                uniqueVisitors: 0,
            };
        }
        weeks[weekStart].visitors += d.visitors;
        weeks[weekStart].pageViews += d.pageViews;
        weeks[weekStart].uniqueVisitors += d.uniqueVisitors;
    }
    return Object.values(weeks);
}

// Traffic sources breakdown
export function getTrafficSources() {
    return [
        { name: "Organic Search", value: 38, color: "#1A56DB" },
        { name: "Direct", value: 24, color: "#059669" },
        { name: "Social Media", value: 18, color: "#F97066" },
        { name: "Referral", value: 12, color: "#0891B2" },
        { name: "Email", value: 5, color: "#D97706" },
        { name: "Paid Ads", value: 3, color: "#7C3AED" },
    ];
}

// Device breakdown
export function getDeviceBreakdown() {
    return [
        { name: "Mobile", value: 52, color: "#1A56DB" },
        { name: "Desktop", value: 38, color: "#059669" },
        { name: "Tablet", value: 10, color: "#F97066" },
    ];
}

// Browser breakdown
export function getBrowserBreakdown() {
    return [
        { name: "Chrome", value: 58, color: "#4285F4" },
        { name: "Safari", value: 22, color: "#5AC8FA" },
        { name: "Firefox", value: 10, color: "#FF7139" },
        { name: "Edge", value: 7, color: "#0078D4" },
        { name: "Other", value: 3, color: "#8C95A4" },
    ];
}

// Geographic data
export function getGeographicData() {
    return [
        { country: "United States", visitors: 4820, flag: "🇺🇸", percent: 32.1 },
        { country: "United Kingdom", visitors: 2310, flag: "🇬🇧", percent: 15.4 },
        { country: "Germany", visitors: 1680, flag: "🇩🇪", percent: 11.2 },
        { country: "France", visitors: 1240, flag: "🇫🇷", percent: 8.3 },
        { country: "Canada", visitors: 980, flag: "🇨🇦", percent: 6.5 },
        { country: "Australia", visitors: 870, flag: "🇦🇺", percent: 5.8 },
        { country: "Netherlands", visitors: 650, flag: "🇳🇱", percent: 4.3 },
        { country: "Israel", visitors: 580, flag: "🇮🇱", percent: 3.9 },
        { country: "Spain", visitors: 440, flag: "🇪🇸", percent: 2.9 },
        { country: "Japan", visitors: 380, flag: "🇯🇵", percent: 2.5 },
    ];
}

// Top pages
export function getTopPages() {
    return [
        { page: "/", title: "Home", views: 8240, unique: 5120, avgTime: "2m 15s" },
        { page: "/add_expense", title: "Add Expense", views: 5680, unique: 3200, avgTime: "4m 32s" },
        { page: "/all_trips", title: "My Trips", views: 4320, unique: 2800, avgTime: "3m 48s" },
        { page: "/suggestions", title: "Suggestions", views: 3150, unique: 2100, avgTime: "5m 10s" },
        { page: "/trip-tools", title: "Trip Tools", views: 2840, unique: 1960, avgTime: "3m 22s" },
        { page: "/weather", title: "Weather", views: 2100, unique: 1500, avgTime: "2m 05s" },
        { page: "/signup", title: "Sign Up", views: 1880, unique: 1700, avgTime: "1m 45s" },
        { page: "/login", title: "Login", views: 1650, unique: 1480, avgTime: "0m 58s" },
    ];
}

// Hourly traffic for today
export function getHourlyTraffic() {
    const rng = seededRandom(99);
    const hours = [];
    for (let h = 0; h < 24; h++) {
        let base;
        if (h >= 0 && h < 6) base = 15 + rng() * 20;
        else if (h >= 6 && h < 9) base = 40 + rng() * 30;
        else if (h >= 9 && h < 12) base = 80 + rng() * 40;
        else if (h >= 12 && h < 14) base = 65 + rng() * 30;
        else if (h >= 14 && h < 18) base = 90 + rng() * 50;
        else if (h >= 18 && h < 21) base = 70 + rng() * 40;
        else base = 35 + rng() * 25;

        hours.push({
            hour: h,
            label: `${h.toString().padStart(2, "0")}:00`,
            visitors: Math.round(base),
        });
    }
    return hours;
}

// Real-time active users (mock)
export function getRealtimeUsers() {
    return Math.round(45 + Math.random() * 30);
}

// Conversion funnel
export function getConversionFunnel() {
    return [
        { step: "Website Visit", value: 15000, color: "#1A56DB" },
        { step: "Signup Page", value: 4200, color: "#0891B2" },
        { step: "Completed Signup", value: 2100, color: "#059669" },
        { step: "Created Trip", value: 1450, color: "#D97706" },
        { step: "Added Expense", value: 980, color: "#F97066" },
    ];
}

// ============================================
// DETAILED DAY REPORT — individual visitor sessions
// ============================================
const FIRST_NAMES = ["James", "Emma", "Liam", "Olivia", "Noah", "Sophia", "Ethan", "Ava", "Mason", "Isabella", "Lucas", "Mia", "Logan", "Charlotte", "Alexander", "Amelia", "Jacob", "Harper", "Daniel", "Evelyn", "Michael", "Abigail", "Benjamin", "Emily", "Sebastian", "Elizabeth", "William", "Sofia", "Henry", "Victoria", "Owen", "Chloe", "Jack", "Grace", "Samuel", "Lily", "Ryan", "Zoe", "Nathan", "Ella", "David", "Aria", "Matthew", "Scarlett", "Joseph", "Hannah", "Andrew", "Nora", "John", "Riley"];
const LAST_NAMES = ["Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis", "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas", "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson", "White", "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson"];
const PAGES = [
    { path: "/", title: "Home" },
    { path: "/all_trips", title: "My Trips" },
    { path: "/add_expense", title: "Add Expense" },
    { path: "/suggestions", title: "Suggestions" },
    { path: "/trip-tools", title: "Trip Tools" },
    { path: "/weather", title: "Weather" },
    { path: "/signup", title: "Sign Up" },
    { path: "/login", title: "Login" },
    { path: "/pricing", title: "Pricing" },
    { path: "/about", title: "About Us" },
    { path: "/blog", title: "Blog" },
    { path: "/contact", title: "Contact" },
    { path: "/faq", title: "FAQ" },
    { path: "/settings", title: "Settings" },
    { path: "/profile", title: "Profile" },
];
const CITIES = ["New York", "London", "Berlin", "Paris", "Tel Aviv", "Toronto", "Sydney", "Tokyo", "Amsterdam", "Madrid", "Rome", "Singapore", "Dubai", "Mumbai", "Bangkok", "Los Angeles", "Chicago", "San Francisco", "Seattle", "Denver"];
const COUNTRIES = ["US", "UK", "DE", "FR", "IL", "CA", "AU", "JP", "NL", "ES", "IT", "SG", "AE", "IN", "TH", "US", "US", "US", "US", "US"];
const DEVICES_LIST = ["Desktop", "Mobile", "Tablet"];
const BROWSERS_LIST = ["Chrome", "Safari", "Firefox", "Edge", "Samsung Internet", "Opera"];
const OS_LIST = ["Windows 11", "macOS Sonoma", "iOS 17", "Android 14", "Ubuntu 22.04", "Windows 10", "macOS Ventura", "iOS 16", "Android 13", "Chrome OS"];
const REFERRERS = ["google.com", "Direct", "facebook.com", "twitter.com", "linkedin.com", "bing.com", "reddit.com", "instagram.com", "youtube.com", "tiktok.com", "pinterest.com", "tripadvisor.com", "booking.com", "expedia.com", "newsletter"];

export function generateDayReport(dateStr) {
    // Use date string as seed for consistent data per day
    const seed = dateStr.split("-").reduce((acc, v) => acc * 31 + parseInt(v), 0);
    const rng = seededRandom(seed);

    const dow = dayjs(dateStr).day();
    const isWeekend = dow === 0 || dow === 6;
    const visitorCount = Math.round(isWeekend ? 80 + rng() * 70 : 140 + rng() * 120);

    const visitors = [];
    for (let i = 0; i < visitorCount; i++) {
        const r = rng;
        const firstName = FIRST_NAMES[Math.floor(r() * FIRST_NAMES.length)];
        const lastName = LAST_NAMES[Math.floor(r() * LAST_NAMES.length)];
        const isAnonymous = r() < 0.35; // 35% anonymous visitors
        const cityIdx = Math.floor(r() * CITIES.length);

        // Pages visited (1-8 pages per session)
        const pageCount = Math.max(1, Math.round(r() * 7 + 0.5));
        const pagesVisited = [];
        const usedPaths = new Set();
        for (let p = 0; p < pageCount; p++) {
            let page;
            let attempts = 0;
            do {
                page = PAGES[Math.floor(r() * PAGES.length)];
                attempts++;
            } while (usedPaths.has(page.path) && attempts < 15);
            usedPaths.add(page.path);
            const timeOnPage = Math.round(10 + r() * 300); // 10-310 seconds per page
            pagesVisited.push({
                ...page,
                timeOnPage,
                timeFormatted: `${Math.floor(timeOnPage / 60)}m ${(timeOnPage % 60).toString().padStart(2, "0")}s`,
            });
        }

        const totalDuration = pagesVisited.reduce((sum, p) => sum + p.timeOnPage, 0);

        // Entry time — distribute across 24h with realistic pattern
        let entryHour;
        const timeRoll = r();
        if (timeRoll < 0.05) entryHour = Math.floor(r() * 5); // 12am-5am (5%)
        else if (timeRoll < 0.15) entryHour = 5 + Math.floor(r() * 3); // 5-8am (10%)
        else if (timeRoll < 0.35) entryHour = 8 + Math.floor(r() * 4); // 8am-12pm (20%)
        else if (timeRoll < 0.45) entryHour = 12 + Math.floor(r() * 2); // 12-2pm (10%)
        else if (timeRoll < 0.7) entryHour = 14 + Math.floor(r() * 4); // 2-6pm (25%)
        else if (timeRoll < 0.9) entryHour = 18 + Math.floor(r() * 3); // 6-9pm (20%)
        else entryHour = 21 + Math.floor(r() * 3); // 9pm-12am (10%)

        const entryMinute = Math.floor(r() * 60);
        const entryTime = `${entryHour.toString().padStart(2, "0")}:${entryMinute.toString().padStart(2, "0")}`;

        const deviceIdx = r() < 0.52 ? 1 : (r() < 0.85 ? 0 : 2); // Mobile heavy
        const isNewUser = r() < 0.4;
        const converted = r() < 0.12; // 12% conversion
        const bounced = pageCount === 1 && r() < 0.5;

        visitors.push({
            id: `v-${dateStr}-${i}`,
            name: isAnonymous ? null : `${firstName} ${lastName}`,
            email: isAnonymous ? null : `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
            isAnonymous,
            city: CITIES[cityIdx],
            country: COUNTRIES[cityIdx],
            device: DEVICES_LIST[deviceIdx],
            browser: BROWSERS_LIST[Math.floor(r() * BROWSERS_LIST.length)],
            os: OS_LIST[Math.floor(r() * OS_LIST.length)],
            referrer: REFERRERS[Math.floor(r() * REFERRERS.length)],
            entryTime,
            entryPage: pagesVisited[0]?.path || "/",
            exitPage: pagesVisited[pagesVisited.length - 1]?.path || "/",
            pagesVisited,
            pageCount,
            totalDuration,
            totalDurationFormatted: `${Math.floor(totalDuration / 60)}m ${(totalDuration % 60).toString().padStart(2, "0")}s`,
            isNewUser,
            converted,
            bounced,
        });
    }

    // Sort by entry time
    visitors.sort((a, b) => a.entryTime.localeCompare(b.entryTime));

    // Summary stats for the day
    const totalPageViews = visitors.reduce((sum, v) => sum + v.pageCount, 0);
    const avgDuration = visitors.length > 0 ? Math.round(visitors.reduce((sum, v) => sum + v.totalDuration, 0) / visitors.length) : 0;
    const bounceCount = visitors.filter(v => v.bounced).length;
    const bounceRate = visitors.length > 0 ? Math.round((bounceCount / visitors.length) * 1000) / 10 : 0;
    const newUsers = visitors.filter(v => v.isNewUser).length;
    const conversions = visitors.filter(v => v.converted).length;
    const conversionRate = visitors.length > 0 ? Math.round((conversions / visitors.length) * 1000) / 10 : 0;

    // Hourly breakdown
    const hourly = Array.from({ length: 24 }, (_, h) => ({
        hour: h,
        label: `${h.toString().padStart(2, "0")}:00`,
        visitors: visitors.filter(v => parseInt(v.entryTime.split(":")[0]) === h).length,
    }));

    // Top pages for this day
    const pageCounts = {};
    visitors.forEach(v => v.pagesVisited.forEach(p => {
        if (!pageCounts[p.path]) pageCounts[p.path] = { ...p, views: 0, totalTime: 0 };
        pageCounts[p.path].views++;
        pageCounts[p.path].totalTime += p.timeOnPage;
    }));
    const dayTopPages = Object.values(pageCounts)
        .map(p => ({ ...p, avgTime: Math.round(p.totalTime / p.views) }))
        .sort((a, b) => b.views - a.views);

    // Device breakdown
    const deviceCounts = {};
    visitors.forEach(v => { deviceCounts[v.device] = (deviceCounts[v.device] || 0) + 1; });
    const deviceBreakdown = Object.entries(deviceCounts).map(([name, count]) => ({
        name, value: Math.round((count / visitors.length) * 100),
        color: name === "Mobile" ? "#1A56DB" : name === "Desktop" ? "#059669" : "#F97066",
    }));

    // Referrer breakdown
    const refCounts = {};
    visitors.forEach(v => { refCounts[v.referrer] = (refCounts[v.referrer] || 0) + 1; });
    const referrerBreakdown = Object.entries(refCounts)
        .map(([name, count]) => ({ name, count, percent: Math.round((count / visitors.length) * 100) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

    return {
        date: dateStr,
        dateFormatted: dayjs(dateStr).format("dddd, MMMM D, YYYY"),
        visitors,
        summary: {
            totalVisitors: visitors.length,
            totalPageViews,
            avgDuration,
            avgDurationFormatted: `${Math.floor(avgDuration / 60)}m ${(avgDuration % 60).toString().padStart(2, "0")}s`,
            bounceRate,
            newUsers,
            returningUsers: visitors.length - newUsers,
            conversions,
            conversionRate,
        },
        hourly,
        topPages: dayTopPages,
        deviceBreakdown,
        referrerBreakdown,
    };
}

// KPI summary for current month vs last month
export function getKPISummary(monthlyData) {
    if (monthlyData.length < 2) return null;
    const current = monthlyData[monthlyData.length - 1];
    const previous = monthlyData[monthlyData.length - 2];

    const pctChange = (curr, prev) => prev === 0 ? 0 : Math.round(((curr - prev) / prev) * 1000) / 10;

    return {
        totalVisitors: {
            value: current.visitors,
            prev: previous.visitors,
            change: pctChange(current.visitors, previous.visitors),
        },
        pageViews: {
            value: current.pageViews,
            prev: previous.pageViews,
            change: pctChange(current.pageViews, previous.pageViews),
        },
        uniqueVisitors: {
            value: current.uniqueVisitors,
            prev: previous.uniqueVisitors,
            change: pctChange(current.uniqueVisitors, previous.uniqueVisitors),
        },
        bounceRate: {
            value: current.avgBounceRate,
            prev: previous.avgBounceRate,
            change: pctChange(current.avgBounceRate, previous.avgBounceRate),
            invertColor: true, // lower is better
        },
        avgSessionDuration: {
            value: current.avgSessionDuration,
            prev: previous.avgSessionDuration,
            change: pctChange(current.avgSessionDuration, previous.avgSessionDuration),
        },
        newUsers: {
            value: current.newUsers,
            prev: previous.newUsers,
            change: pctChange(current.newUsers, previous.newUsers),
        },
    };
}
